import { differenceInCalendarDays } from './differenceInCalendarDays';
import {
  defaultLocales,
  minutesInHour,
  secondsInMinute,
  milisecondsInSecond,
  daysInWeek,
} from './constants';
import { dateTimeString } from './dateTimeString';
import { isInvalidDate } from './isInvalidDate';
import { dayOfWeekString } from './dayOfWeekString';
import { dateOnlyString } from './dateOnlyString';

/**
 * Shows time ago relative to current time.
 *
 * examples:
 *
 *   < 1 minute ago
 *
 *   33 minutes ago
 *
 *   Today, 3:30 PM
 *
 *   Yesterday, 6:22 AM
 *
 *   Nov 11, 11:32 AM
 *
 *   Feb 23, 2020, 1:55 PM
 *
 *  or when `includeTime` is false:
 *
 *   Today
 *
 *   Yesterday
 *
 *   on Thursday
 *
 *   Nov 11
 *
 *   Feb 23, 2020
 *
 * NOTE: timeAgoString doesn't support multiple time zones since doing so would
 * complicate calculations for whether to use "Today" or "Yesterday".
 * @param {Date} date - the date relative to now
 * @param {object} options -
 * @param {Date} options.nowAnchor - The date used to calculate relative to now. Defaults to current date but can be overwritten for tests.
 * @param {boolean} options.includeTime - Whether to include the time in the output. Defaults to true.
 * @returns {string} - a string representation of the date.
 */
export const timeAgoString = (
  date: Date,
  { nowAnchor = new Date(), includeTime = true }: TimeAgoOptions = {},
): string => {
  if (isInvalidDate(date)) {
    return '';
  }

  const minutesAgo =
    (nowAnchor.valueOf() - date.valueOf()) / (secondsInMinute * milisecondsInSecond);
  const minutesAgoRounded = Math.round(minutesAgo);
  const differenceInDays = differenceInCalendarDays(nowAnchor, date);
  const isSameYear = date.getFullYear() === nowAnchor.getFullYear();

  // handle edge case where date is in future
  if (minutesAgo < 0) {
    return dateTimeString(date);
  }

  if (minutesAgo < 1) {
    return '< 1 minute ago';
  }

  if (minutesAgoRounded < 2) {
    return '1 minute ago';
  }

  if (minutesAgoRounded <= minutesInHour) {
    return `${minutesAgoRounded} minutes ago`;
  }
  if (differenceInDays === 0) {
    return includeTime
      ? `Today, ${Intl.DateTimeFormat(defaultLocales, { hour: 'numeric', minute: '2-digit' }).format(date)}`
      : 'Today';
  }

  if (differenceInDays === 1) {
    return includeTime
      ? `Yesterday, ${Intl.DateTimeFormat(defaultLocales, { hour: 'numeric', minute: '2-digit' }).format(date)}`
      : 'Yesterday';
  }

  if (differenceInDays < daysInWeek && !includeTime) {
    return `on ${dayOfWeekString(date)}`;
  }

  return includeTime
    ? dateTimeString(date, { omitYear: isSameYear })
    : dateOnlyString(date, { omitYear: false });
};
